<template>
    <Head title="Nominate raters" />
    <BaseLayout>
        <a :href="backHref" class="self-start body-small text-valence-grey-750 hover:text-valence-grey-850 flex items-center gap-1">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
        </a>

        <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <template v-else-if="cycle">
            <BasePageHeader :title="mode === 'draft' ? 'Who would you like to hear from?' : mode === 'add-more' ? 'Add more raters' : 'Invited raters'">
                <template v-if="mode === 'draft'" #eyebrow>
                    <AssessmentEyebrow>Step 1 of 2</AssessmentEyebrow>
                </template>
                <template #subtitle>
                    <template v-if="mode === 'draft'">
                        <template v-if="thresholds !== null">
                            <template v-if="cycle.subject_specifies_relationship">Add colleagues and tell us each person's relationship to you.</template>
                            <template v-else>Add colleagues. Each will tell us their relationship to you — you don't categorize them.</template>
                        </template>
                        <template v-else>
                            Add at least {{ cycle.min_responses }} colleagues. Each will tell us their relationship to you (peer, manager, direct report, etc.) — you don't
                            categorize them.
                        </template>
                    </template>
                    <template v-else-if="mode === 'add-more'">
                        Your 360 is collecting responses. You can keep adding colleagues until it closes{{ closesAtLabel ? ` on ${closesAtLabel}` : "" }}; each new rater gets an
                        invite email right away.
                    </template>
                    <template v-else>This 360 has closed and no longer accepts new raters.</template>
                </template>
            </BasePageHeader>

            <AssessmentThresholdsCallout
                v-if="thresholds !== null && mode !== 'read-only'"
                :progress="thresholds"
                :show-invited="showInvitedCounts"
                :show-submitted="showSubmittedCounts" />

            <StatusChip v-if="mode === 'draft'" :state="meetsMin ? 'done' : 'pending'" size="md" class="self-start">
                <template #leading>
                    <i :class="canLaunch ? 'bi bi-check-circle-fill' : 'bi bi-people'" aria-hidden="true"></i>
                </template>
                {{ addedChipLabel }}
            </StatusChip>

            <!-- Existing invites -->
            <BaseCard v-if="invites.length > 0" padding="none">
                <div class="px-4 py-3 border-b border-valence-grey-100 body-small-bold text-valence-grey-750">Invited</div>
                <ul class="divide-y divide-valence-grey-100">
                    <li v-for="invite in invites" :key="invite.id" class="px-4 py-3 flex items-center gap-3">
                        <BaseAvatar size="sm" theme="neutral"><i class="bi bi-person" aria-hidden="true"></i></BaseAvatar>
                        <div class="flex-1">
                            <div class="body-small-semibold text-valence-grey-850">{{ invite.rater.display_name }}</div>
                            <div class="details text-valence-grey-750">{{ invite.rater.email }}</div>
                            <div v-if="invite.last_reminder_at" class="details text-valence-grey-600 mt-0.5">
                                Last reminded {{ formatInviteNotificationDate(invite.last_reminder_at) }}
                            </div>
                            <div v-else-if="invite.invited_at" class="details text-valence-grey-600 mt-0.5">
                                Requested review at {{ formatInviteNotificationDate(invite.invited_at) }}
                            </div>
                        </div>
                        <button
                            v-if="canActOnInvite(cycle, invite) && mode === 'add-more'"
                            type="button"
                            class="px-2 py-1 rounded-md border border-valence-grey-200 details font-medium text-valence-grey-750 hover:bg-valence-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            :disabled="resendingId === invite.id || isResendDisabled(invite)"
                            :title="isResendDisabled(invite) ? 'You can resend once per day' : 'Resend invite email'"
                            @click="resendInvite(invite)">
                            <i class="bi" :class="resendingId === invite.id ? 'bi-hourglass-split' : resentIds.has(invite.id) ? 'bi-check2' : 'bi-envelope'" aria-hidden="true"></i>
                            {{ resendingId === invite.id ? "Sending…" : resentIds.has(invite.id) ? "Sent" : "Resend" }}
                        </button>
                        <AssessmentInviteCopyMenu v-if="invite.invite_token && mode !== 'draft'" :invite="invite" :cycle="cycle" />
                        <StatusChip v-if="invite.state" :state="inviteStateChipState(invite.state)">{{ invite.state }}</StatusChip>
                        <button v-if="canRemove(invite)" class="text-valence-grey-700 hover:text-red-600 px-2" title="Remove" @click="removeInvite(invite.id)">
                            <i class="bi bi-x-lg" aria-hidden="true"></i>
                        </button>
                    </li>
                </ul>
            </BaseCard>

            <!-- Add a rater: autocompletes Nadia users, or invite an external email -->
            <BaseCard v-if="mode !== 'read-only'" padding="md">
                <label for="rater-search" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-1">Add a rater</span>
                    <span class="block details text-valence-grey-750 mb-2">Search by name, or enter an email address to invite someone who hasn't used Nadia yet.</span>
                    <span class="relative block">
                        <input
                            id="rater-search"
                            v-model="query"
                            placeholder="Search by name or email"
                            class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                            @input="onQueryChange"
                            @focus="onQueryChange"
                            @keydown="handleKeydown" />
                        <ul v-if="options.length > 0" class="absolute z-10 mt-1 w-full bg-white border border-valence-grey-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            <li v-for="(option, index) in options" :key="option.kind === 'user' ? option.rater.uuid : `external:${option.email}`">
                                <button
                                    :ref="(el) => setItemRef(el, index)"
                                    type="button"
                                    class="w-full text-left px-3 py-2 cursor-pointer flex items-center gap-2"
                                    :class="{ 'bg-valence-grey-50': index === highlightedIndex }"
                                    @click="selectOption(option)"
                                    @mouseenter="highlightedIndex = index"
                                    @mouseleave="highlightedIndex = -1"
                                    @focus="highlightedIndex = index"
                                    @blur="highlightedIndex = -1">
                                    <template v-if="option.kind === 'user'">
                                        <BaseAvatar size="xs" theme="neutral"><i class="bi bi-person text-xs" aria-hidden="true"></i></BaseAvatar>
                                        <div class="flex-1">
                                            <div class="body-small-semibold text-valence-grey-850">{{ option.rater.display_name }}</div>
                                            <div class="details text-valence-grey-750">{{ option.rater.email }}</div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <BaseAvatar size="xs" theme="neutral"><i class="bi bi-envelope text-xs" aria-hidden="true"></i></BaseAvatar>
                                        <div class="flex-1">
                                            <div class="body-small-semibold text-valence-grey-850">{{ addingExternal ? "Inviting…" : `Invite ${option.email}` }}</div>
                                            <div class="details text-valence-grey-750">Hasn't used Nadia yet</div>
                                        </div>
                                    </template>
                                </button>
                            </li>
                        </ul>
                    </span>
                </label>
            </BaseCard>

            <!-- Personal note (already saved at create-time, but editable here) -->
            <BaseCard v-if="cycle.personal_note" theme="accent" padding="md">
                <div class="body-small-bold text-valence-grey-850 mb-1">Note to raters</div>
                <p class="body-small text-valence-grey-850">"{{ cycle.personal_note }}"</p>
            </BaseCard>

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <div v-if="mode === 'draft'" class="flex items-center justify-between pt-2">
                <div class="body-small text-valence-grey-750">Next: complete your self-assessment.</div>
                <BaseButton theme="primary" :disabled="!canLaunch || launching" @click="onLaunch">
                    {{ launching ? "Launching…" : "Launch 360" }}
                </BaseButton>
            </div>
            <div v-else class="flex items-center justify-end pt-2">
                <a :href="cycleHref" class="base-btn-primary">Done</a>
            </div>
        </template>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleForSubject, InviteAnonymized, InviteEnvelopeResponse, SearchRatersResponse, UserMinimal } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import AssessmentInviteCopyMenu from "~vue/components/AssessmentInviteCopyMenu.vue";
import AssessmentThresholdsCallout from "~vue/components/AssessmentThresholdsCallout.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useAssessmentInviteResend } from "~vue/composables/useAssessmentInviteResend";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { withLiveNominations } from "~vue/utils/categoryThresholds";
import { logUserInteraction } from "~vue/utils/logUtils";
import { canActOnInvite, inviteStateChipState } from "~vue/utils/threeSixty";
import type { ComponentPublicInstance } from "vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";

const props = defineProps<{
    cycle_id: string;
}>();

const cycle = ref<CycleForSubject | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const invites = ref<InviteAnonymized[]>([]);
const query = ref("");
const searchResults = ref<UserMinimal[]>([]);
const highlightedIndex = ref(-1);
const itemRefs = ref<HTMLElement[]>([]);
const launching = ref(false);
const error = ref<string | null>(null);
const addingExternal = ref(false);
const isValidEmail = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.value.trim()));

// The dropdown is a single list of Nadia-user matches plus, when the input is
// an email with no exact Nadia match, an "invite external" option at the end.
type RaterOption = { kind: "user"; rater: UserMinimal } | { kind: "external"; email: string };

const options = computed<RaterOption[]>(() => {
    const opts: RaterOption[] = searchResults.value.map((rater) => ({ kind: "user", rater }));
    const trimmed = query.value.trim();
    if (isValidEmail.value && !searchResults.value.some((r) => r.email.toLowerCase() === trimmed.toLowerCase())) {
        opts.push({ kind: "external", email: trimmed });
    }
    return opts;
});

function setItemRef(el: Element | ComponentPublicInstance | null, index: number) {
    if (el instanceof HTMLElement) {
        itemRefs.value[index] = el;
    }
}

watch(options, () => {
    highlightedIndex.value = -1;
    itemRefs.value = [];
});

watch(highlightedIndex, async (index) => {
    if (index < 0) {
        return;
    }
    await nextTick();
    itemRefs.value[index]?.scrollIntoView({ block: "nearest" });
});

// Nominate is the resume target for DRAFT cycles, so Back goes to the hub —
// linking to /reflections/360/<id> would just redirect right back here.
const backHref = "/reflections";
function canRemove(invite: InviteAnonymized) {
    return mode.value !== "read-only" && canActOnInvite(cycle.value, invite);
}

const pendingCount = computed(() => invites.value.filter((i) => i.state === "pending").length);
const thresholds = computed(() => {
    const stored = cycle.value?.category_thresholds ?? null;
    return stored === null ? null : withLiveNominations(stored, invites.value);
});
// Invites only carry a category when the subject picks it, so nomination
// progress is unknowable while raters categorize themselves.
const nominationProgress = computed(() => (cycle.value?.subject_specifies_relationship ? thresholds.value : null));
// Invites carry no category until the rater answers when raters categorize
// themselves, so those cycles have no per-category invite count to show.
const showInvitedCounts = computed(() => nominationProgress.value !== null);
// Responses only exist once the cycle is collecting, and they are what unlocks
// the report, so from then on they decide whether a category reads as met.
const showSubmittedCounts = computed(() => mode.value !== "draft");

const meetsMin = computed(() => pendingCount.value >= (cycle.value?.min_responses ?? 0));
// Category thresholds gate the report, not the launch: a subject short of a
// category still launches and keeps inviting while the cycle collects.
const canLaunch = computed(() => (thresholds.value === null ? meetsMin.value : pendingCount.value > 0));

// Only rendered for cycles without category thresholds; the thresholds callout
// carries the progress otherwise.
const addedChipLabel = computed(() => {
    const missing = (cycle.value?.min_responses ?? 0) - pendingCount.value;
    return `${pendingCount.value} of minimum ${cycle.value?.min_responses ?? 0} added${missing > 0 ? ` — add ${missing} more` : ""}`;
});

// `mode` drives all UI variants: DRAFT keeps the launch flow; COLLECTING /
// UNLOCKED cycles allow adding more raters; CLOSED cycles show a read-only
// view.
const mode = computed<"draft" | "add-more" | "read-only">(() => {
    if (cycle.value === null) {
        return "draft";
    }
    if (cycle.value.state === "draft") {
        return "draft";
    }
    if (cycle.value.state === "closed") {
        return "read-only";
    }
    return "add-more";
});

const closesAtLabel = computed<string | null>(() => {
    if (cycle.value == null || cycle.value.closes_at == null) {
        return null;
    }
    const closesAt = new Date(cycle.value.closes_at);
    if (Number.isNaN(closesAt.getTime())) {
        return null;
    }
    return closesAt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
});

const cycleHref = computed<string>(() => `/reflections/360/${props.cycle_id}`);

const { resendingId, resentIds, resendInvite, isResendDisabled, formatInviteNotificationDate } = useAssessmentInviteResend({
    cycleId: props.cycle_id,
    setError: (message) => {
        error.value = message;
    },
    onInviteUpdated: (inviteId, updatedInvite) => {
        const idx = invites.value.findIndex((invite) => invite.id === inviteId);
        if (idx !== -1) {
            invites.value[idx] = updatedInvite;
        }
    },
});

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}`).json<CycleEnvelopeResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load cycle.";
        return;
    }
    const loaded = data.value?.cycle;
    if (!loaded) {
        loadError.value = "Could not load cycle.";
        return;
    }
    if (loaded.is_self_assessment_only) {
        router.visit(`/reflections/360/${props.cycle_id}`);
        return;
    }
    cycle.value = loaded;
    invites.value = [...loaded.invites];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_NOMINATE_VIEWED, {
        cycle_id: props.cycle_id,
        current_invite_count: invites.value.length,
        min_responses: loaded.min_responses,
    });
});

let searchTimer: ReturnType<typeof setTimeout> | null = null;

function onQueryChange() {
    if (searchTimer) {
        clearTimeout(searchTimer);
    }
    const q = query.value.trim();
    if (q.length < 2) {
        searchResults.value = [];
        return;
    }
    searchTimer = setTimeout(async () => {
        const { data } = await useFetch(`/v1/reflections/raters/search?q=${encodeURIComponent(q)}&cycle_id=${props.cycle_id}`).json<SearchRatersResponse>();
        searchResults.value = data.value?.results ?? [];
    }, 200);
}

function selectOption(option: RaterOption) {
    if (option.kind === "user") {
        void addInvite(option.rater);
    } else {
        void addExternalInvite(option.email);
    }
}

function handleKeydown(e: KeyboardEvent) {
    if (options.value.length === 0) {
        return;
    }
    if (e.key === "ArrowDown") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value + 1) % options.value.length;
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value - 1 + options.value.length) % options.value.length;
    } else if (e.key === "Enter") {
        if (highlightedIndex.value >= 0) {
            e.preventDefault();
            selectOption(options.value[highlightedIndex.value]);
        } else {
            // No explicit highlight: Enter invites an email-only entry as an
            // external rater, but never auto-adds the first name match.
            const external = options.value.find((o) => o.kind === "external");
            if (external) {
                e.preventDefault();
                selectOption(external);
            }
        }
    } else if (e.key === "Escape") {
        e.preventDefault();
        searchResults.value = [];
    }
}

async function addInvite(rater: UserMinimal) {
    error.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/invites`)
        .post(JSON.stringify({ rater_uuid: rater.uuid }), "application/json")
        .json<InviteEnvelopeResponse>();

    if (fetchError.value) {
        error.value = fetchError.value.message || "Could not add this rater.";
        return;
    }

    const invite = data.value?.invite;
    if (!invite) {
        error.value = "Could not add this rater.";
        return;
    }
    invites.value.push(invite);
    query.value = "";
    searchResults.value = [];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_ADDED, {
        cycle_id: props.cycle_id,
        invite_count_after: invites.value.length,
    });
}

async function removeInvite(inviteId: string) {
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/invites/${inviteId}`).delete().json();
    if (fetchError.value) {
        error.value = "Could not remove this rater.";
        return;
    }
    invites.value = invites.value.filter((i) => i.id !== inviteId);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_REMOVED, {
        cycle_id: props.cycle_id,
        invite_count_after: invites.value.length,
    });
}

async function addExternalInvite(email: string) {
    if (addingExternal.value) {
        return;
    }
    addingExternal.value = true;
    error.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/external-invites`)
        .post(JSON.stringify({ rater_email: email }), "application/json")
        .json<InviteEnvelopeResponse>();
    addingExternal.value = false;

    if (fetchError.value) {
        error.value = fetchError.value.message || "Could not invite this rater.";
        return;
    }
    const invite = data.value?.invite;
    if (!invite) {
        error.value = "Could not invite this rater.";
        return;
    }
    invites.value.push(invite);
    query.value = "";
    searchResults.value = [];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_ADDED, {
        cycle_id: props.cycle_id,
        invite_count_after: invites.value.length,
        is_external: true,
    });
}

async function onLaunch() {
    if (!canLaunch.value || launching.value || mode.value !== "draft") {
        return;
    }
    launching.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/launch`).post().json<CycleEnvelopeResponse>();
        if (fetchError.value) {
            error.value = fetchError.value.message || "Could not launch cycle.";
            return;
        }
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CYCLE_LAUNCHED, {
            cycle_id: props.cycle_id,
            rater_count: pendingCount.value,
            self_assess_enabled: cycle.value?.self_assess_enabled ?? false,
        });
        const next = cycle.value?.self_assess_enabled ? `/reflections/360/${props.cycle_id}/self` : `/reflections/360/${props.cycle_id}`;
        router.visit(next);
    } finally {
        launching.value = false;
    }
}
</script>
